import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Contract } from './Contract'
import { Field } from './Field'
import { Item } from './Item'
import { Option } from './Option'
import { RecordDataMap } from './RecordDataMap'
import { FKType } from './SQLBuilder'
import { Type } from './Type'
import { User } from './User'

type TriggerConstructorArgs = ConstructorArgs<() => Trigger>

export class Trigger extends BaseRecord {
    readonly recordType = 'trigger'

    type?: string
    label?: string
    message?: string
    discount?: number
    discountType?: string
    startDate?: moment.Moment

    days?: string
    autoStop?: boolean

    audience?: string
    audienceCount?: number // Integer
    audienceAmount?: number
    previewCount?: number // Integer
    pause?: boolean

    pausedDate?: moment.Moment
    cancelledDate?: moment.Moment
    completedDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<TriggerConstructorArgs>) {
        super(data)
        if (!data) {
            this.previewCount = 0
            this.owner = PSApplication.owner()
            return
        }

        this.label = data.label ?? undefined
        this.message = data.message ?? undefined
        this.type = data.type ?? undefined
        this.days = data.days ?? undefined
        this.autoStop = data.auto_stop ?? false
        this.discount = this.propertyFromCurrencyArg(data.discount)
        this.discountType = data.discount_type ?? undefined
        this.startDate = this.propertyFromDateArg(data.start_date)
        this.audience = data.audience ?? undefined
        this.audienceCount = this.propertyFromIntArg(data.audience_count)
        this.audienceAmount = this.propertyFromCurrencyArg(data.audience_amount)
        this.pausedDate = this.propertyFromTimestampArg(data.paused_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.completedDate = this.propertyFromTimestampArg(data.completed_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<TriggerConstructorArgs>): Trigger {
        return new Trigger({ ...this.serializeForClone(), ...overrides })
    }

    get typeString(): string | undefined {
        const type = this.type
        for (const option of Trigger.typeStrings(undefined)) {
            if (option.id === type) return option.label
        }
        return undefined
    }

    static typeStrings(me?: User): Option[] {
        const strings: Option[] = []
        strings.push(
            new Option(undefined, {
                id: 'flash',
                label: 'Make announcement',
                group: 'Most popular',
            })
        ) //, image: "dashboard_messages.png"}))
        if (!me || me.sms) {
            strings.push(
                new Option(undefined, {
                    id: 'phone',
                    label: 'Respond to missed call',
                    group: 'Most popular',
                })
            ) //, image: "new_transaction_send_via.png"}))
            strings.push(
                new Option(undefined, {
                    id: 'message',
                    label: 'Respond to missed text',
                    group: 'Most popular',
                })
            ) //, image: "new_transaction_send_via.png"}))
        }
        strings.push(
            new Option(undefined, {
                id: 'welcome',
                label: 'Welcome new client',
                group: 'Client management',
            })
        ) //, image: "new_transaction_client.png"}))
        strings.push(
            new Option(undefined, {
                id: 'lead',
                label: 'Send lead form follow-up',
                group: 'Client management',
            })
        ) //, image: "settings_leads.png"}))
        strings.push(
            new Option(undefined, {
                id: 'birthday',
                label: 'Wish happy birthday',
                group: 'Client management',
            })
        ) //, image: "new_transaction_client.png"}))
        strings.push(
            new Option(undefined, {
                id: 'anniversary',
                label: 'Send anniversary reminder',
                group: 'Client management',
            })
        ) //, image: "new_transaction_client.png"}))
        strings.push(
            new Option(undefined, {
                id: 'churn',
                label: 'Reach out to past client',
                group: 'Client management',
            })
        ) //, image: "settings_notifications.png"}))
        strings.push(
            new Option(undefined, {
                id: 'activate',
                label: 'Convert to paying client',
                group: 'Client management',
            })
        ) //, image: "settings_payment.png"}))
        strings.push(
            new Option(undefined, {
                id: 'overdue',
                label: 'Collect overdue payment',
                group: 'Client management',
            })
        ) //, image: "settings_payment.png"}))
        strings.push(
            new Option(undefined, {
                id: 'complete',
                label: 'Send after appointment',
                group: 'Scheduling',
            })
        ) //, image: "settings_calendar.png"}))
        strings.push(
            new Option(undefined, {
                id: 'confirm',
                label: 'Send prep for appointment',
                group: 'Scheduling',
            })
        ) //, image: "settings_calendar.png"}))
        if (!me || me.allowPackage) {
            strings.push(
                new Option(undefined, {
                    id: 'expire',
                    label: 'Renew expired package',
                    group: 'Packages',
                })
            ) //, image: "settings_packages.png"}))
            strings.push(
                new Option(undefined, {
                    id: 'expiring',
                    label: 'Renew expiring package',
                    group: 'Packages',
                })
            ) //, image: "settings_packages.png"}))
            strings.push(
                new Option(undefined, {
                    id: 'exhaust',
                    label: 'Renew exhausted package',
                    group: 'Packages',
                })
            ) //, image: "settings_packages.png"}))
        }
        if (!me || me.allowPackage || me.allowProduct || me.allowSubscription)
            strings.push(
                new Option(undefined, {
                    id: 'purchase',
                    label: 'Purchase follow-up',
                    group: 'Purchases',
                })
            ) //, image: "settings_payment.png"}))
        if (!me || me.allowProduct)
            strings.push(
                new Option(undefined, {
                    id: 'fulfill',
                    label: 'Completed order follow-up',
                    group: 'Purchases',
                })
            ) //, image: "dashboard_orders.png"}))
        if (!me || me.allowRecord)
            strings.push(
                new Option(undefined, {
                    id: 'form',
                    label: 'Completed form follow-up',
                    group: 'Documents',
                })
            ) //, image: "settings_forms.png"}))
        if (!me || me.allowContract)
            strings.push(
                new Option(undefined, {
                    id: 'contract',
                    label: 'Signed contract follow-up',
                    group: 'Documents',
                })
            ) //, image: "settings_contracts.png"}))
        return strings
    }

    static helpString(type: string): string {
        if (type === 'activate')
            return 'Reach out to non-paying clients and turn them into paying clients.'
        else if (type === 'welcome')
            return 'Reach out to newly added clients so they can easily book, pay, and message you.'
        else if (type === 'lead')
            return 'Reach out to new leads who have completed your online lead form and turn them into active leads or more.'
        else if (type === 'complete')
            return 'Request a review, send follow up instructions and turn new clients into repeat clients.'
        else if (type === 'churn')
            return 'Reach out to past clients and get them to book and buy from you again.'
        else if (type === 'purchase')
            return 'Encourage these clients to check out your other services.'
        else if (type === 'confirm')
            return 'Send custom reminders, forms to complete, and appointment prep instructions.'
        else if (type === 'fulfill')
            return 'Send satisfaction surveys, product usage instructions, and re-order reminders.'
        else if (type === 'call') return 'Send an automated message when you miss a call.'
        else if (type === 'message')
            return 'Send an automated message when you are away from the app for 8h or more.'
        else if (type === 'overdue') return 'Send a custom reminder to clients who owe you money.'
        else if (type === 'contract')
            return 'Send a custom confirmation to clients who have signed your contract.'
        else if (type === 'form')
            return 'Send a custom confirmation to clients who have completed your form.'
        else if (type === 'expire') return 'Get clients whose packages have expired to buy again.'
        else if (type === 'expiring')
            return 'Get clients whose packages are expiring soon to renew.'
        else if (type === 'exhaust')
            return 'Get clients whose packages are out of sessions to renew.'
        else if (type === 'birthday') return 'Wish your clients Happy Birthday.'
        else if (type === 'anniversary')
            return 'Remind your clients of upcoming anniversaries or vaccinations.'
        return 'Send special announcements, promotions to your clients based on their purchase history.'
    }

    static dayStrings(type: string): Option[] {
        const strings: Option[] = []
        if (type === 'churn') {
            strings.push(new Option(undefined, { id: '7', label: '7 days' }))
            strings.push(new Option(undefined, { id: '14', label: '14 days' }))
            strings.push(new Option(undefined, { id: '30', label: '30 days' }))
            strings.push(new Option(undefined, { id: '45', label: '45 days' }))
            strings.push(new Option(undefined, { id: '60', label: '60 days' }))
            strings.push(new Option(undefined, { id: '70', label: '10 weeks' }))
            strings.push(new Option(undefined, { id: '90', label: '90 days' }))
            strings.push(new Option(undefined, { id: '1m', label: '1 month' }))
            strings.push(new Option(undefined, { id: '2m', label: '2 months' }))
            strings.push(new Option(undefined, { id: '3m', label: '3 months' }))
            strings.push(new Option(undefined, { id: '6m', label: '6 months' }))
            strings.push(new Option(undefined, { id: '9m', label: '9 months' }))
            strings.push(new Option(undefined, { id: '12m', label: '1 year' }))
        }
        else if (type === 'confirm') {
            strings.push(new Option(undefined, { id: '', label: 'Send immediately' }))
            strings.push(new Option(undefined, { id: '1', label: '1 day' }))
            strings.push(new Option(undefined, { id: '2', label: '2 days' }))
            strings.push(new Option(undefined, { id: '3', label: '3 days' }))
            strings.push(new Option(undefined, { id: '7', label: '7 days' }))
            strings.push(new Option(undefined, { id: '10', label: '10 days' }))
            strings.push(new Option(undefined, { id: '14', label: '2 weeks' }))
            strings.push(new Option(undefined, { id: '1m', label: '1 months' }))
            strings.push(new Option(undefined, { id: '2m', label: '2 months' }))
            strings.push(new Option(undefined, { id: '70', label: '10 weeks' }))
            strings.push(new Option(undefined, { id: '3m', label: '3 months' }))
            strings.push(new Option(undefined, { id: '4m', label: '4 months' }))
            strings.push(new Option(undefined, { id: '5m', label: '5 months' }))
            strings.push(new Option(undefined, { id: '6m', label: '6 months' }))
            strings.push(new Option(undefined, { id: '7m', label: '7 months' }))
            strings.push(new Option(undefined, { id: '8m', label: '8 months' }))
            strings.push(new Option(undefined, { id: '9m', label: '9 months' }))
            strings.push(new Option(undefined, { id: '10m', label: '10 months' }))
            strings.push(new Option(undefined, { id: '11m', label: '11 months' }))
            strings.push(new Option(undefined, { id: '12m', label: '1 year' }))
        }
        else if (type === 'complete' || type === 'fulfill') {
            strings.push(new Option(undefined, { id: '', label: 'Send immediately' }))
            strings.push(new Option(undefined, { id: '1', label: '1 day' }))
            strings.push(new Option(undefined, { id: '2', label: '2 days' }))
            strings.push(new Option(undefined, { id: '3', label: '3 days' }))
            strings.push(new Option(undefined, { id: '7', label: '7 days' }))
            strings.push(new Option(undefined, { id: '10', label: '10 days' }))
            strings.push(new Option(undefined, { id: '14', label: '2 weeks' }))
            strings.push(new Option(undefined, { id: '1m', label: '1 months' }))
            strings.push(new Option(undefined, { id: '2m', label: '2 months' }))
            strings.push(new Option(undefined, { id: '70', label: '10 weeks' }))
            strings.push(new Option(undefined, { id: '3m', label: '3 months' }))
            strings.push(new Option(undefined, { id: '4m', label: '4 months' }))
            strings.push(new Option(undefined, { id: '5m', label: '5 months' }))
            strings.push(new Option(undefined, { id: '6m', label: '6 months' }))
            strings.push(new Option(undefined, { id: '7m', label: '7 months' }))
            strings.push(new Option(undefined, { id: '8m', label: '8 months' }))
            strings.push(new Option(undefined, { id: '9m', label: '9 months' }))
            strings.push(new Option(undefined, { id: '10m', label: '10 months' }))
            strings.push(new Option(undefined, { id: '11m', label: '11 months' }))
            strings.push(new Option(undefined, { id: '12m', label: '1 year' }))
            strings.push(new Option(undefined, { id: '24m', label: '2 years' }))
        }
        else {
            if (
                type === 'welcome' ||
                type === 'contract' ||
                type === 'form' ||
                type === 'purchase' ||
                type === 'activate' ||
                type === 'lead'
            )
                strings.push(new Option(undefined, { id: '', label: 'Send immediately' }))
            strings.push(new Option(undefined, { id: '1', label: '1 day' }))
            strings.push(new Option(undefined, { id: '2', label: '2 days' }))
            strings.push(new Option(undefined, { id: '3', label: '3 days' }))
            strings.push(new Option(undefined, { id: '4', label: '4 days' }))
            strings.push(new Option(undefined, { id: '5', label: '5 days' }))
            strings.push(new Option(undefined, { id: '6', label: '6 days' }))
            strings.push(new Option(undefined, { id: '7', label: '7 days' }))
            strings.push(new Option(undefined, { id: '10', label: '10 days' }))
            strings.push(new Option(undefined, { id: '14', label: '2 weeks' }))
            strings.push(new Option(undefined, { id: '21', label: '3 weeks' }))
            strings.push(new Option(undefined, { id: '28', label: '4 weeks' }))
            strings.push(new Option(undefined, { id: '30', label: '30 days' }))
        }
        return strings
    }

    static budgetStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: 'None' }))
        strings.push(new Option(undefined, { id: '5', label: '$5 (50 credits)' }))
        strings.push(new Option(undefined, { id: '10', label: '$10 (100 credits)' }))
        strings.push(new Option(undefined, { id: '20', label: '$20 (200 credits)' }))
        strings.push(new Option(undefined, { id: '50', label: '$50 (600 credits)' }))
        strings.push(new Option(undefined, { id: '100', label: '$100 (1200 credits)' }))
        return strings
    }

    static audienceStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: 'all_clients', label: 'All clients' }))
        strings.push(new Option(undefined, { id: 'active_clients', label: 'Active clients' }))
        strings.push(new Option(undefined, { id: 'churned_clients', label: 'Past clients' }))
        strings.push(new Option(undefined, { id: 'bought_this_item', label: 'Bought {item}' }))
        strings.push(
            new Option(undefined, { id: 'not_bought_this_item', label: 'Has not bought {item}' })
        )
        strings.push(
            new Option(undefined, { id: 'spent_more_than', label: 'Spent more than {amount}' })
        )
        strings.push(
            new Option(undefined, { id: 'spent_less_than', label: 'Spent less than {amount}' })
        )
        strings.push(
            new Option(undefined, {
                id: 'bought_multiple_times',
                label: 'Bought {item} more than once',
            })
        )
        strings.push(
            new Option(undefined, { id: 'bought_one_time', label: 'Bought {item} only once' })
        )
        return strings
    }

    static audienceCountStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '1', label: '1' }))
        strings.push(new Option(undefined, { id: '2', label: '2' }))
        strings.push(new Option(undefined, { id: '3', label: '3' }))
        strings.push(new Option(undefined, { id: '4', label: '4' }))
        strings.push(new Option(undefined, { id: '5', label: '5' }))
        strings.push(new Option(undefined, { id: '6', label: '6' }))
        strings.push(new Option(undefined, { id: '7', label: '7' }))
        strings.push(new Option(undefined, { id: '10', label: '10' }))
        strings.push(new Option(undefined, { id: '14', label: '14' }))
        strings.push(new Option(undefined, { id: '21', label: '21' }))
        strings.push(new Option(undefined, { id: '28', label: '28' }))
        strings.push(new Option(undefined, { id: '30', label: '30' }))
        return strings
    }

    static audienceAmountStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '20', label: '$20' }))
        strings.push(new Option(undefined, { id: '50', label: '$50' }))
        strings.push(new Option(undefined, { id: '100', label: '$100' }))
        strings.push(new Option(undefined, { id: '200', label: '$200' }))
        strings.push(new Option(undefined, { id: '500', label: '$500' }))
        strings.push(new Option(undefined, { id: '1000', label: '$1000' }))
        strings.push(new Option(undefined, { id: '5000', label: '$5000' }))
        strings.push(new Option(undefined, { id: '10000', label: '$10000' }))
        strings.push(new Option(undefined, { id: '20000', label: '$20000' }))
        return strings
    }

    static defaultStrings(): { [key: string]: string } {
        let d: { [key: string]: string } = {}
        d['activate'] =
            '{firstname}, this is an app for my clients to book, pay, & text me! Enjoy a 10% discount on your next booking. Tap on the link below to access: {checkout_link}. Text or call me at {me.sms}'
        d['welcome'] =
            '{firstname}, I use an app to schedule & message with clients.\nIf you would like to use it to track our sessions, please download it from the App Store. If not, no worries, you can always book me here {checkout_link}. Text or call us at {me.sms}.'
        d['complete'] =
            '{firstname}, we hope you enjoyed your recent visit. If you book your next visit in the next 24 hours, you get 10% off. Tap on the link below to access: {checkout_link}. Text or call me at {me.sms}'
        d['fulfill'] =
            '{firstname}, your order was just fulfilled! Tap on the link below to view pickup or tracking information for your order: {order_link}. To track the status of your shipment please use this tracking code {tracking_code}. If you have any questions, text or call me at {me.sms}'
        d['churn'] =
            '{firstname}, it has been {days_since_last_appointment} since your last appointment. You get 10% off your next booking. Tap the link below to access: {checkout_link}. Text or call me at {me.sms}.'
        d['flash'] =
            '{firstname}, we hope you & your family enjoy the holiday. With this 10% FLASH SALE discount, you can book any service here {checkout_link}. This promotion will be live for the next 72 hours.'
        d['purchase'] =
            '{firstname}, we look forward to working with you. Here’s a special discount. This promotion will expire in 3 days. Tap my booking link below to access: {checkout_link}. Text or call me at {me.sms}.'
        d['overdue'] =
            '{firstname}, thank you for being a valued client. We are closing our books for the month. We need to close all open invoices. Tap the link below to pay {overdue_link}. Text or call me at {me.sms}.'
        d['contract'] =
            '{firstname}, thank you for signing the contract. See below a signed copy of the agreement {contract_link}. We look forward to working with you.'
        d['form'] =
            '{firstname}, thank you for completing our form. See below a copy of the completed form for your records {form_link}. We look forward to working with you.'
        d['expire'] =
            '{firstname}, it was such a pleasure working with you! Your package has expired. To buy a new one, tap the link below & purchase it {checkout_link}. Once you buy, we can schedule your next session.'
        d['expiring'] =
            '{firstname}, it was a pleasure working with you! Your package is expiring in {days}. To buy a new one, tap the link below & purchase it {checkout_link}. Once you buy, we’ll book more your sessions.'
        d['exhaust'] =
            '{firstname}, it was such a pleasure working with you! You have used up your package. To buy a new one, tap the link below & purchase it {checkout_link}. Once you buy, let’s book more sessions.'
        d['birthday'] =
            "{firstname}, Happy Birthday! Here's a special treat for your special day, tap the link below to book your next session and claim your birthday treat {checkout_link}."
        d['anniversary'] =
            "{firstname}, Happy Anniversary! Here's a special treat for your special day, tap the link below to book your next session and claim your anniversary treat {checkout_link}."
        d['confirm'] =
            '{firstname}, we look forward to your upcoming appointment! In preparation for our time together, I have included some additional information below {booking_link}. If you have any questions before our appointment, feel free to text or call me at {me.sms}.'
        d['phone'] =
            '{firstname}, you have reached {me}. Sorry I missed your call. If you are interested in booking an appointment with me, please visit my website at {checkout_link}. Feel free to text me at {me.sms}.'
        d['message'] =
            '{firstname}, you have reached {me}. I will respond to your text shortly. If you are interested in scheduling, please visit my website at {checkout_link}. Feel free to text me at {me.sms}.'
        d['lead'] =
            '{firstname}, Welcome to {me.company}! We are excited to work with you. We know it isn’t easy to take this first step with you. We would love to connect and discuss your goals as well as share more about how we can support you. Please give us a call at {me.sms} and we’ll begin the process. I have also included my booking site if you already are clear about where you would like to start. Feel free to tap the link below to schedule and get 20% off of your first appointment. {checkout_link}'
        return d
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.item) this.item = BaseRecord.fromSerializer(data.item, Item)
        if (data.discountItem)
            this.discountItem = BaseRecord.fromSerializer(data.discountItem, Item)
        if (data.form) this.form = BaseRecord.fromSerializer(data.form, Type)
        if (data.field) this.field = BaseRecord.fromSerializer(data.field, Field)
        if (data.contract) this.contract = BaseRecord.fromSerializer(data.contract, Contract)
        if (data.audienceItem)
            this.audienceItem = BaseRecord.fromSerializer(data.audienceItem, Item)
        if (data.startDate) this.startDate = moment(data.startDate)
        if (data.pausedDate) this.pausedDate = moment(data.pausedDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.completedDate) this.completedDate = moment(data.completedDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner'],
        items: ['item', 'discountItem', 'audienceItem'],
        types: ['form'],
        fields: ['field'],
        contracts: ['contract'],
    }

    static dataType(): DataType {
        return 'triggers'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['type'] = this.type ?? ''
        dict['label'] = this.label ?? ''
        dict['message'] = this.message ?? ''
        dict['days'] = this.days ?? ''
        dict['item'] = this.item?.id ?? ''
        dict['form'] = this.form?.id ?? ''
        dict['field'] = this.field?.id ?? ''
        dict['contract'] = this.contract?.id ?? ''
        dict['auto_stop'] = this.autoStop
        dict['owner'] = this.owner?.id ?? ''
        dict['discount'] = this.discount ?? ''
        dict['discount_type'] = this.discountType ?? ''
        dict['discount_item'] = this.discountItem?.id ?? ''
        if (this.type === 'flash') dict['start_date'] = this.startDate?.format('YYYY-MM-DD')
        if (!this.id) dict['pause'] = this.pause
        dict['audience'] = this.audience ?? ''
        dict['audience_item'] = this.audienceItem?.id ?? ''
        dict['audience_amount'] = this.audienceAmount ?? ''
        dict['audience_count'] = this.audienceCount ?? ''
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get item(): Item | undefined {
        return this.getForeignRecord('item', Item)
    }
    set item(item: Item | undefined) {
        this.setForeignRecord('item', item)
    }
    get form(): Type | undefined {
        return this.getForeignRecord('form', Type)
    }
    set form(form: Type | undefined) {
        this.setForeignRecord('form', form)
    }
    get field(): Field | undefined {
        return this.getForeignRecord('field', Field)
    }
    set field(field: Field | undefined) {
        this.setForeignRecord('field', field)
    }
    get contract(): Contract | undefined {
        return this.getForeignRecord('contract', Contract)
    }
    set contract(contract: Contract | undefined) {
        this.setForeignRecord('contract', contract)
    }
    get discountItem(): Item | undefined {
        return this.getForeignRecord('discount_item', Item)
    }
    set discountItem(discountItem: Item | undefined) {
        this.setForeignRecord('discount_item', discountItem)
    }
    get audienceItem(): Item | undefined {
        return this.getForeignRecord('audience_item', Item)
    }
    set audienceItem(audienceItem: Item | undefined) {
        this.setForeignRecord('audience_item', audienceItem)
    }
}
